import { inject, Injectable } from '@angular/core';
import { BehaviorSubject, map, Observable } from 'rxjs';
import { AuthService } from '../../core/auth/auth.service';
import { ApiGetParams, ApiService } from '../../core/transport/api.service';
import { Endpoints } from '../../core/transport/endpoints';
import { IPaginated } from '../../core/types/http.interface';
import {
  CustomerSiteRequestAdminFilter,
  ICustomerSiteRequest,
  ICustomerSiteRequestCreatePayload,
  ICustomerSiteRequestDecisionPayload,
} from '../../core/types/customer-site-request.interface';
import {
  ICustomer,
  ICustomerCreatePayload,
  ICustomerMergePayload,
  ICustomerMergeResult,
  ICustomerProfile,
  ICustomerProfileActivity,
  ICustomerProfileActivityPayload,
  ICustomerProfileAdminQueueItem,
  ICustomerProfileAnalytics,
  ICustomerProfileAuditEvent,
  ICustomerProfileApprovalSlaReminderPayload,
  ICustomerProfileComplaint,
  ICustomerProfileComplaintDecisionPayload,
  ICustomerProfileComplaintPayload,
  ICustomerProfileComplaintResponsePayload,
  ICustomerProfileComplianceSummary,
  ICustomerProfileCreatePayload,
  ICustomerProfileDecisionPayload,
  ICustomerProfileMoreInfoPayload,
  ICustomerProfilePricingPayload,
  ICustomerProfileRenewal,
  ICustomerProfileRenewalDecisionPayload,
  ICustomerProfileRenewalPayload,
  ICustomerProfileRevokePayload,
  ICustomerProfileStatusRequest,
  ICustomerProfileStatusRequestCureNoticePayload,
  ICustomerProfileStatusRequestPayload,
  ICustomerProfileStatusUpdate,
  ICustomerProfileStatusUpdatePayload,
  ICustomerProfileTerminateResellerPayload,
  ICustomerProfileTerminateResellerResult,
  ICustomerProfileTransferPayload,
  ICustomerProfileUpdatePayload,
} from '../../core/types/customer-profile.interface';
import { IPartNumber } from '../../core/types/part-number.interface';

@Injectable({
  providedIn: 'root',
})
export class CustomerProfilesService {
  private readonly api = inject(ApiService);
  private readonly authService = inject(AuthService);

  readonly customerProfiles$ = new BehaviorSubject<IPaginated<ICustomerProfile[]> | null>(null);

  reset(): void {
    this.customerProfiles$.next(null);
  }

  findAll(params: ApiGetParams = {}): void {
    this.reset();

    this.findMany(params).subscribe({
      next: (response: IPaginated<ICustomerProfile[]>) => {
        this.customerProfiles$.next(response);
      },
      error: () => {},
    });
  }

  findMany(params: ApiGetParams = {}): Observable<IPaginated<ICustomerProfile[]>> {
    return this.api.get<IPaginated<ICustomerProfile[]>>(this.listEndpoint, params);
  }

  findApprovedRegistrations(params: ApiGetParams = {}): Observable<IPaginated<ICustomerProfile[]>> {
    return this.findMany({
      limit: 100500,
      'filter[status]': 'approved',
      ...params,
    });
  }

  findOne(customerProfileId: string): Observable<ICustomerProfile> {
    return this.api.get<ICustomerProfile>(this.oneEndpoint(customerProfileId));
  }

  createOne(payload: ICustomerProfileCreatePayload): Observable<ICustomerProfile> {
    return this.api.post<ICustomerProfile>(this.listEndpoint, payload);
  }

  updateOne(customerProfileId: string, payload: ICustomerProfileUpdatePayload): Observable<ICustomerProfile> {
    return this.api.put<ICustomerProfile>(Endpoints.customerProfiles.adminOne(customerProfileId), payload);
  }

  decideOne(customerProfileId: string, payload: ICustomerProfileDecisionPayload): Observable<ICustomerProfile> {
    return this.api.patch<ICustomerProfile>(Endpoints.customerProfiles.adminDecision(customerProfileId), payload);
  }

  updatePricing(customerProfileId: string, payload: ICustomerProfilePricingPayload): Observable<ICustomerProfile> {
    return this.api.put<ICustomerProfile>(Endpoints.customerProfiles.adminPricing(customerProfileId), payload);
  }

  deleteOne(customerProfileId: string): Observable<void> {
    return this.api.delete<void>(Endpoints.customerProfiles.adminOne(customerProfileId));
  }

  findPricingPartNumbers(): Observable<IPartNumber[]> {
    return this.api.get<IPartNumber[]>(Endpoints.partNumbers.adminList);
  }

  // Customers

  searchCustomers(query = '', limit = 20): Observable<ICustomer[]> {
    return this.api.get<ICustomer[]>(this.customersSearchEndpoint, {
      q: query,
      limit,
    });
  }

  createCustomer(payload: ICustomerCreatePayload): Observable<ICustomer> {
    return this.api.post<ICustomer>(Endpoints.customers.create, payload);
  }

  mergeAdminCustomer(customerId: string, payload: ICustomerMergePayload): Observable<ICustomerMergeResult> {
    return this.api.post<ICustomerMergeResult>(Endpoints.customers.adminMerge(customerId), payload);
  }

  // Admin queue and analytics

  getAdminQueue(params: ApiGetParams = {}): Observable<IPaginated<ICustomerProfileAdminQueueItem[]>> {
    return this.api.get<IPaginated<ICustomerProfileAdminQueueItem[]>>(
      Endpoints.customerProfiles.adminQueue,
      params
    );
  }

  getAdminQueueItem(profileId: string): Observable<ICustomerProfileAdminQueueItem> {
    return this.api.get<ICustomerProfileAdminQueueItem>(Endpoints.customerProfiles.adminQueueOne(profileId));
  }

  getAdminAnalytics(params: ApiGetParams = {}): Observable<ICustomerProfileAnalytics> {
    return this.api.get<ICustomerProfileAnalytics>(Endpoints.customerProfiles.adminAnalytics, params);
  }

  // Compliance

  getCompliance(profileId: string): Observable<ICustomerProfileComplianceSummary> {
    return this.api.get<ICustomerProfileComplianceSummary>(this.complianceEndpoint(profileId));
  }

  getAuditTrail(profileId: string): Observable<ICustomerProfileAuditEvent[]> {
    return this.api
      .get<IPaginated<ICustomerProfileAuditEvent[]>>(this.auditEndpoint(profileId), { limit: 100 })
      .pipe(map((response: IPaginated<ICustomerProfileAuditEvent[]>) => response.items));
  }

  // Activities

  getActivities(profileId: string, params: ApiGetParams = {}): Observable<IPaginated<ICustomerProfileActivity[]>> {
    return this.api.get<IPaginated<ICustomerProfileActivity[]>>(this.activitiesEndpoint(profileId), params);
  }

  createActivity(profileId: string, payload: ICustomerProfileActivityPayload): Observable<ICustomerProfileActivity> {
    return this.api.post<ICustomerProfileActivity>(this.activitiesEndpoint(profileId), payload);
  }

  updateActivity(profileId: string, activityId: string, payload: ICustomerProfileActivityPayload): Observable<ICustomerProfileActivity> {
    return this.api.patch<ICustomerProfileActivity>(this.activityEndpoint(profileId, activityId), payload);
  }

  deleteActivity(profileId: string, activityId: string): Observable<void> {
    return this.api.delete<void>(this.activityEndpoint(profileId, activityId));
  }

  // Status Updates

  getStatusUpdates(profileId: string, params: ApiGetParams = {}): Observable<IPaginated<ICustomerProfileStatusUpdate[]>> {
    return this.api.get<IPaginated<ICustomerProfileStatusUpdate[]>>(this.statusUpdatesEndpoint(profileId), params);
  }

  createStatusUpdate(profileId: string, payload: ICustomerProfileStatusUpdatePayload): Observable<ICustomerProfileStatusUpdate> {
    return this.api.post<ICustomerProfileStatusUpdate>(this.statusUpdatesEndpoint(profileId), payload);
  }

  // Status Requests

  getStatusRequests(
    profileId: string,
    params: ApiGetParams = {}
  ): Observable<IPaginated<ICustomerProfileStatusRequest[]>> {
    return this.api.get<IPaginated<ICustomerProfileStatusRequest[]>>(
      Endpoints.customerProfiles.adminStatusRequests(profileId),
      params
    );
  }

  createStatusRequest(
    profileId: string,
    payload: ICustomerProfileStatusRequestPayload = {}
  ): Observable<ICustomerProfileStatusRequest> {
    return this.api.post<ICustomerProfileStatusRequest>(
      Endpoints.customerProfiles.adminStatusRequests(profileId),
      payload
    );
  }

  issueStatusRequestCureNotice(
    profileId: string,
    statusRequestId: string,
    payload: ICustomerProfileStatusRequestCureNoticePayload = {}
  ): Observable<ICustomerProfileStatusRequest> {
    return this.api.patch<ICustomerProfileStatusRequest>(
      Endpoints.customerProfiles.adminStatusRequestCureNotice(profileId, statusRequestId),
      payload
    );
  }

  // Renewals

  getRenewals(profileId: string, params: ApiGetParams = {}): Observable<IPaginated<ICustomerProfileRenewal[]>> {
    return this.api.get<IPaginated<ICustomerProfileRenewal[]>>(this.renewalsEndpoint(profileId), params);
  }

  createRenewal(profileId: string, payload: ICustomerProfileRenewalPayload): Observable<ICustomerProfileRenewal> {
    return this.api.post<ICustomerProfileRenewal>(this.renewalsEndpoint(profileId), payload);
  }

  decideRenewal(profileId: string, renewalId: string, payload: ICustomerProfileRenewalDecisionPayload): Observable<ICustomerProfileRenewal> {
    return this.api.patch<ICustomerProfileRenewal>(
      Endpoints.customerProfiles.adminRenewalDecision(profileId, renewalId),
      payload
    );
  }

  // Complaints

  getComplaints(profileId: string, params: ApiGetParams = {}): Observable<IPaginated<ICustomerProfileComplaint[]>> {
    return this.api.get<IPaginated<ICustomerProfileComplaint[]>>(this.complaintsEndpoint(profileId), params);
  }

  createComplaint(profileId: string, payload: ICustomerProfileComplaintPayload): Observable<ICustomerProfileComplaint> {
    return this.api.post<ICustomerProfileComplaint>(
      Endpoints.customerProfiles.adminComplaints(profileId),
      payload
    );
  }

  respondComplaint(
    profileId: string,
    complaintId: string,
    payload: ICustomerProfileComplaintResponsePayload
  ): Observable<ICustomerProfileComplaint> {
    return this.api.patch<ICustomerProfileComplaint>(
      Endpoints.customerProfiles.complaintResponse(profileId, complaintId),
      payload
    );
  }

  decideComplaint(
    profileId: string,
    complaintId: string,
    payload: ICustomerProfileComplaintDecisionPayload
  ): Observable<ICustomerProfileComplaint> {
    return this.api.patch<ICustomerProfileComplaint>(
      Endpoints.customerProfiles.adminComplaintDecision(profileId, complaintId),
      payload
    );
  }

  // Admin actions

  requestMoreInfo(profileId: string, payload: ICustomerProfileMoreInfoPayload): Observable<ICustomerProfile> {
    return this.api.patch<ICustomerProfile>(Endpoints.customerProfiles.adminMoreInfo(profileId), payload);
  }

  transferProfile(profileId: string, payload: ICustomerProfileTransferPayload): Observable<ICustomerProfile> {
    return this.api.patch<ICustomerProfile>(Endpoints.customerProfiles.adminTransfer(profileId), payload);
  }

  revokeProfile(profileId: string, payload: ICustomerProfileRevokePayload): Observable<ICustomerProfile> {
    return this.api.patch<ICustomerProfile>(Endpoints.customerProfiles.adminRevoke(profileId), payload);
  }

  notifyApprovalSlaBreached(
    profileId: string,
    payload: ICustomerProfileApprovalSlaReminderPayload = {}
  ): Observable<ICustomerProfile> {
    return this.api.post<ICustomerProfile>(
      Endpoints.customerProfiles.adminApprovalSlaReminder(profileId),
      payload
    );
  }

  terminateReseller(
    payload: ICustomerProfileTerminateResellerPayload
  ): Observable<ICustomerProfileTerminateResellerResult> {
    return this.api.post<ICustomerProfileTerminateResellerResult>(
      Endpoints.customerProfiles.adminTerminateReseller,
      payload
    );
  }

  // Customer site requests

  getSiteRequests(): Observable<ICustomerSiteRequest[]> {
    return this.api.get<ICustomerSiteRequest[]>(Endpoints.customerProfiles.siteRequests);
  }

  createSiteRequest(payload: ICustomerSiteRequestCreatePayload): Observable<ICustomerSiteRequest> {
    return this.api.post<ICustomerSiteRequest>(Endpoints.customerProfiles.siteRequests, payload);
  }

  getAdminSiteRequests(status: CustomerSiteRequestAdminFilter = 'pending'): Observable<ICustomerSiteRequest[]> {
    return this.api.get<ICustomerSiteRequest[]>(
      Endpoints.customerProfiles.adminSiteRequests,
      { status },
    );
  }

  decideAdminSiteRequest(
    requestId: string,
    payload: ICustomerSiteRequestDecisionPayload
  ): Observable<ICustomerSiteRequest> {
    return this.api.patch<ICustomerSiteRequest>(
      Endpoints.customerProfiles.adminSiteRequestDecision(requestId),
      payload
    );
  }

  private get customersSearchEndpoint(): string {
    return this.authService.hasCustomerProfilesAdminAccessRuntimeSync
      ? Endpoints.customers.adminList
      : Endpoints.customers.search;
  }

  private get listEndpoint(): string {
    return this.authService.hasCustomerProfilesAdminAccessRuntimeSync
      ? Endpoints.customerProfiles.adminList
      : Endpoints.customerProfiles.list;
  }

  private oneEndpoint(profileId: string): string {
    return this.authService.hasCustomerProfilesAdminAccessRuntimeSync
      ? Endpoints.customerProfiles.adminOne(profileId)
      : Endpoints.customerProfiles.one(profileId);
  }

  private complianceEndpoint(profileId: string): string {
    return this.authService.hasCustomerProfilesAdminAccessRuntimeSync
      ? Endpoints.customerProfiles.adminCompliance(profileId)
      : Endpoints.customerProfiles.compliance(profileId);
  }

  private auditEndpoint(profileId: string): string {
    return this.authService.hasCustomerProfilesAdminAccessRuntimeSync
      ? Endpoints.customerProfiles.adminAudit(profileId)
      : Endpoints.customerProfiles.audit(profileId);
  }

  private activitiesEndpoint(profileId: string): string {
    return this.authService.hasCustomerProfilesAdminAccessRuntimeSync
      ? Endpoints.customerProfiles.adminActivities(profileId)
      : Endpoints.customerProfiles.activities(profileId);
  }

  private activityEndpoint(profileId: string, activityId: string): string {
    return this.authService.hasCustomerProfilesAdminAccessRuntimeSync
      ? Endpoints.customerProfiles.adminActivity(profileId, activityId)
      : Endpoints.customerProfiles.activity(profileId, activityId);
  }

  private statusUpdatesEndpoint(profileId: string): string {
    return this.authService.hasCustomerProfilesAdminAccessRuntimeSync
      ? Endpoints.customerProfiles.adminStatusUpdates(profileId)
      : Endpoints.customerProfiles.statusUpdates(profileId);
  }

  private complaintsEndpoint(profileId: string): string {
    return this.authService.hasCustomerProfilesAdminAccessRuntimeSync
      ? Endpoints.customerProfiles.adminComplaints(profileId)
      : Endpoints.customerProfiles.complaints(profileId);
  }

  private renewalsEndpoint(profileId: string): string {
    return this.authService.hasCustomerProfilesAdminAccessRuntimeSync
      ? Endpoints.customerProfiles.adminRenewals(profileId)
      : Endpoints.customerProfiles.renewals(profileId);
  }
}
